import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import OnboardingCompleteModal from './OnboardingCompleteModal';
import SubscriptionPlansModal from './SubscriptionPlansModal';
import { startFreeTrial } from '../api';
import { useSelector } from 'react-redux';

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  header: {
    background: '#2c58ab',
    padding: '24px 32px',
    color: '#ffffff'
  },
  content: {
    padding: '32px'
  },
  footer: {
    padding: '24px 32px',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #e5e7eb'
  },
  progressBarContainer: {
    marginTop: '24px'
  },
  progressBar: {
    height: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '9999px',
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '9999px',
    transition: 'width 0.8s ease-out'
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    marginBottom: '12px'
  },
  checklistItemCompleted: {
    border: '2px solid #bbf7d0',
    backgroundColor: '#f0fdf4'
  },
  checklistItemHover: {
    transform: 'scale(1.02)',
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff'
  },
  primaryButton: {
    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
    color: '#ffffff',
    padding: '12px 24px',
    borderRadius: '12px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  primaryButtonHover: {
    transform: 'scale(1.02)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
  }
};

// Configuration for onboarding steps
const ONBOARDING_STEPS = [
  {
    id: 'company_profile',
    title: 'Complete Company Profile',
    description: 'Fill in your company details',
    path: (id) => `/company/edit/${id}`,
    completed: (data, profileData) => data?.is_basic_profile_complete == true
  },
  {
    id: 'add_teacher',
    title: 'Add Teacher',
    description: 'Add at least one teacher to your team',
    path: '/add-teacher',
    completed: (data, profileData) => profileData?.has_teacher === true
  },
  {
    id: 'add_region',
    title: 'Add Region',
    description: 'Set up your service regions',
    path: '/add/area-code',
    completed: (data, profileData) => profileData?.has_region === true
  },
  {
    id: 'add_location',
    title: 'Add Location',
    description: 'Add your venue/locations',
    path: '/add-school',
    completed: (data, profileData) => profileData?.has_location === true
  },
  {
    id: 'add_activity_type',
    title: 'Add Activity Type',
    description: 'Configure your activity types',
    path: '/add-discipline',
    completed: (data, profileData) => profileData?.has_activity_type === true
  },
  {
    id: 'parental_consent',
    title: 'Configure Parental Consent Content',
    description: 'Set up parental consent templates',
    path: '/add-consent',
    completed: (data, profileData) => profileData?.has_parental_consent === true
  },
  {
    id: 'terms_conditions',
    title: 'Configure Terms & Conditions Content',
    description: 'Set up your terms and conditions',
    path: '/add/company-terms-and-conditions',
    completed: (data, profileData) => profileData?.has_terms_conditions === true
  }
];

const OnboardingModal = ({ isOpen, onClose, companyData, authState, profileCompletionData }) => {
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState({});
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  const companyId = useSelector((store) => store.main.company_id);
  const authCompanyId = useSelector((store) => store.auth?.company_id);
  const company_id = authCompanyId || companyId;

  // Determine completion status for each step
  useEffect(() => {
    if (companyData && profileCompletionData) {
      const stepsStatus = {};
      ONBOARDING_STEPS.forEach(step => {
        stepsStatus[step.id] = step.completed(companyData, profileCompletionData);
      });
      setCompletedSteps(stepsStatus);
    }
  }, [companyData, profileCompletionData]);

  // Calculate progress percentage
  const progressPercentage = Math.round(
    (Object.values(completedSteps).filter(Boolean).length / ONBOARDING_STEPS.length) * 100
  );

  // Check if all steps are completed
  const isAllComplete = progressPercentage === 100;

  // Handle celebration animation
  useEffect(() => {
    if (isAllComplete && isOpen) {
      setShowCelebration(true);
      setShowCompleteModal(true);
    }
  }, [isAllComplete, isOpen]);

  // Handle step click
  const handleStepClick = (step) => {
    const path = typeof step.path === 'function' 
      ? step.path(companyData?.id || authState?.company_id)
      : step.path;
    navigate(path);
    onClose();
  };

  // Handle close with animation
  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose();
      setIsAnimatingOut(false);
    }, 300);
  };

  // Handle continue button
  const handleContinue = () => {
    // Find first incomplete step
    const firstIncomplete = ONBOARDING_STEPS.find(step => !completedSteps[step.id]);
    if (firstIncomplete) {
      handleStepClick(firstIncomplete);
    } else {
      handleClose();
    }
  };

  // Handle go to dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard');
    handleClose();
  };

  const handleStartTrial = async () => {
    await startFreeTrial(company_id);
  };

  const handlePurchaseSubscription = () => {
    setShowSubscriptionModal(true);
  };

  if (!isOpen && !isAnimatingOut) return null;

  return (
    <>
      {ReactDOM.createPortal(
        <AnimatePresence>
          {(isOpen || isAnimatingOut) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={styles.backdrop}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                style={styles.modal}
              >
            {/* Header */}
            <div style={styles.header}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', color: '#ffffff' }}>
                      {isAllComplete ? (
                        <motion.span
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 200, damping: 10 }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <i className="fa fa-star"></i>
                          Congratulations!
                        </motion.span>
                      ) : (
                        "Welcome! Your account is almost ready."
                      )}
                    </h2>
                  </motion.div>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                  >
                    {isAllComplete
                      ? "Your account setup is complete. You now have full access to the platform."
                      : "Complete the following setup steps to unlock full access to the platform."}
                  </motion.p>
                </div>
                <button
                  onClick={handleClose}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '50%',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              {/* Progress Bar */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={styles.progressBarContainer}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>Setup Progress</span>
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{progressPercentage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    style={styles.progressBarFill}
                  />
                </div>
              </motion.div>
            </div>

            {/* Content */}
            <div style={styles.content}>
              {isAllComplete ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 15 }}
                  style={{ textAlign: 'center', padding: '48px 0' }}
                >
                  <motion.div
                    animate={{ 
                      rotate: [0, 360],
                      scale: [1, 1.2, 1]
                    }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                    style={{ display: 'inline-block', marginBottom: '24px' }}
                  >
                    <i className="fa fa-check-circle" style={{ fontSize: '96px', color: '#22c55e' }}></i>
                  </motion.div>
                  <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '12px' }}>
                    All Set!
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '32px' }}>
                    You've completed all the required setup steps.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleGoToDashboard}
                    style={styles.primaryButton}
                    onMouseEnter={(e) => Object.assign(e.target.style, styles.primaryButtonHover)}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = styles.primaryButton.boxShadow;
                    }}
                  >
                    Go to Dashboard
                  </motion.button>
                </motion.div>
              ) : (
                /* Checklist View */
                <div>
                  {ONBOARDING_STEPS.map((step, index) => {
                    const isCompleted = completedSteps[step.id];
                    return (
                      <motion.div
                        key={step.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + (index * 0.1) }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        onClick={() => !isCompleted && handleStepClick(step)}
                        style={{
                          ...styles.checklistItem,
                          ...(isCompleted ? styles.checklistItemCompleted : {})
                        }}
                        onMouseEnter={(e) => {
                          if (!isCompleted) {
                            Object.assign(e.target.style, styles.checklistItemHover);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCompleted) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.borderColor = styles.checklistItem.border;
                            e.target.style.backgroundColor = styles.checklistItem.backgroundColor;
                          }
                        }}
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5 + (index * 0.1), type: "spring", stiffness: 200 }}
                          style={{ flexShrink: 0 }}
                        >
                          {isCompleted ? (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                              <i className="fa fa-check-circle" style={{ fontSize: '24px', color: '#22c55e' }}></i>
                            </motion.div>
                          ) : (
                            <i className="fa fa-circle-o" style={{ fontSize: '24px', color: '#9ca3af' }}></i>
                          )}
                        </motion.div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontWeight: '600', color: isCompleted ? '#15803d' : '#111827', marginBottom: '4px' }}>
                            {step.title}
                          </h4>
                          <p style={{ fontSize: '14px', color: isCompleted ? '#16a34a' : '#6b7280' }}>
                            {step.description}
                          </p>
                        </div>
                        {!isCompleted && (
                          <motion.div
                            whileHover={{ x: 5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          >
                            <i className="fa fa-chevron-right" style={{ fontSize: '20px', color: '#9ca3af' }}></i>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {!isAllComplete && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                style={styles.footer}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContinue}
                  style={styles.primaryButton}
                  onMouseEnter={(e) => Object.assign(e.target.style, styles.primaryButtonHover)}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = styles.primaryButton.boxShadow;
                  }}
                >
                  Continue Setup
                  <i className="fa fa-chevron-right" style={{ fontSize: '20px' }}></i>
                </motion.button>
                <p style={{ textAlign: 'center', fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>
                  Click on any step above to complete it, or use the button to continue
                </p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
      <OnboardingCompleteModal 
        isOpen={showCompleteModal} 
        onClose={() => setShowCompleteModal(false)}
        onStartTrial={handleStartTrial}
        onPurchaseSubscription={handlePurchaseSubscription}
        isTrialExpired={companyData?.trial_end && new Date() > new Date(companyData.trial_end)}
        companyId={company_id}
        hasBespokeEnquiry={companyData?.has_bespoke_enquiry == 1}
      />
      <SubscriptionPlansModal 
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </>
  );
};

export default OnboardingModal;
